import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, ChevronLeft, ChevronRight } from 'lucide-react';

const PigmentedLesion = () => {
  const navigate = useNavigate();
  const [currentBenefitIndex, setCurrentBenefitIndex] = useState(0);

  const scrollToContact = () => {
    navigate('/');
    setTimeout(() => {
      const element = document.getElementById('contact');
      if (element) {
        const offset = 90;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;
        
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }, 100);
  };

  const benefits = [
    {
      title: 'Targets Multiple Types of Pigmented Lesions',
      description: 'Effectively treats age spots, sun spots, freckles, melasma, and other forms of hyperpigmentation.'
    },
    {
      title: 'Suitable for Various Body Areas',
      description: 'Can be used on face, hands, chest, arms, and other areas affected by pigmentation.'
    },
    {
      title: 'Non-Invasive Approach',
      description: 'Uses advanced laser technology to target pigmented cells without damaging surrounding tissue.'
    },
    {
      title: 'Minimal Discomfort During Treatment',
      description: 'Most patients experience minimal discomfort, often described as a mild snapping sensation.'
    },
    {
      title: 'Effective on Large Area Coverage',
      description: 'Can treat multiple lesions and larger areas of pigmentation in a single session.'
    },
    {
      title: 'Promotes Even Skin Tone',
      description: 'Reduces dark spots and discoloration for a more uniform, balanced complexion.'
    },
    {
      title: 'Customizable to Individual Needs',
      description: 'Treatment parameters can be adjusted based on skin type, lesion type, and desired results.'
    },
    {
      title: 'Boosts Skin Clarity and Radiance',
      description: 'Reveals brighter, clearer skin as pigmented lesions fade and skin tone evens out.'
    },
    {
      title: 'Helps Reverse Signs of Sun Damage',
      description: 'Addresses photodamage and sun-induced pigmentation for a more youthful appearance.'
    },
    {
      title: 'Enhances Overall Skin Appearance',
      description: 'Improves overall skin quality and confidence by eliminating unwanted pigmentation.'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Pigmented Lesion Removal - Age Spots & Sun Damage | Century Aesthetics</title>
        <meta name="description" content="Laser treatment for age spots, sun damage, and hyperpigmentation. Even skin tone in Forest Hills, NY." />
        <meta name="keywords" content="pigmented lesion removal, age spot treatment, sun damage, hyperpigmentation, laser skin treatment" />
        <link rel="canonical" href="https://www.centuryaesthetics.org/pigmented-lesion" />
        
        <meta property="og:title" content="Pigmented Lesion Removal | Century Longevity & Aesthetics" />
        <meta property="og:description" content="Laser treatment for age spots, sun damage, and hyperpigmentation. Even skin tone in Forest Hills, NY." />
        <meta property="og:url" content="https://www.centuryaesthetics.org/pigmented-lesion" />
      </Helmet>

      <div className="min-h-screen bg-white pt-28">
      
      {/* Main Content Section */}
      <section className="py-12">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="rounded-lg overflow-hidden shadow-2xl h-[500px]">
                <img 
                  src="/pigment.png" 
                  alt="Pigmented Lesion Reduction Treatment" 
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-4xl font-heading font-bold text-olive-darkest mb-6">
                LASER PIGMENTED LESION REMOVAL FOR CLEAR, EVEN SKIN TONE
              </h2>
              <div className="w-20 h-1 bg-gold mb-6"></div>
              
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  Pigmented lesion reduction uses advanced laser technology to effectively eliminate dark spots, age spots, sun damage, freckles, and melasma for a clearer, more even complexion. This non-invasive treatment delivers precise wavelengths of light that target excess melanin in pigmented areas, breaking it down into smaller particles that your body naturally eliminates. The surrounding healthy skin remains unharmed, making this a safe and effective solution for various types of hyperpigmentation on the face, hands, chest, and other body areas.
                </p>
                <p>
                  Most patients achieve optimal results with multiple treatment sessions spaced several weeks apart, depending on the type, size, and depth of pigmentation. After treatment, pigmented areas may appear slightly darker before gradually fading over 1-2 weeks as the fragmented pigment is eliminated. Minimal downtime is required, and proper sun protection is essential to maintain results and prevent new pigmentation. Schedule a consultation at Century Longevity & Aesthetics to discover how pigmented lesion reduction can restore your skin's natural radiance.
                </p>
              </div>

              <motion.button
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.98 }}
                onClick={scrollToContact}
                className="mt-8 bg-olive-darkest text-white px-8 py-4 rounded font-medium text-lg tracking-wide hover:bg-gold hover:text-olive-darkest transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                Book Now
              </motion.button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-12 bg-olive-darkest">
        <div className="max-w-[1400px] mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-heading font-bold text-white mb-4">Pigmented Lesion Reduction Pricing</h2>
            <div className="w-20 h-1 bg-gold mx-auto"></div>
          </motion.div>

          <div className="max-w-md mx-auto bg-white rounded-lg p-12 shadow-2xl">
            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gold/10 flex items-center justify-center">
                <svg 
                  className="w-12 h-12 text-gold" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={1.5} 
                    d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" 
                  />
                </svg>
              </div>
              
              <h3 className="text-2xl font-heading font-normal text-olive-darkest mb-3 tracking-wider">
                Pigmented Lesion Reduction
              </h3>
              <p className="text-4xl font-heading font-light text-gold mb-2 tracking-tight">Contact Us</p>
              <p className="text-lg font-body text-gray-600 mb-8">Pricing varies based on treatment area and lesion type</p>
              
              <motion.button
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.98 }}
                onClick={scrollToContact}
                className="w-full bg-olive-darkest text-white px-8 py-4 rounded-lg font-medium text-lg tracking-wide hover:bg-gold hover:text-olive-darkest transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                Book Now
              </motion.button>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-[1400px] mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-heading font-bold text-olive-darkest mb-4">Benefits of Pigmented Lesion Reduction</h2>
            <div className="w-20 h-1 bg-gold mx-auto"></div>
          </motion.div>

          <div className="max-w-4xl mx-auto">
            {/* Carousel Container */}
            <div className="relative">
              {/* Previous Button */}
              <button
                onClick={() => setCurrentBenefitIndex((prev) => (prev === 0 ? benefits.length - 1 : prev - 1))}
                className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 sm:-translate-x-12 z-10 bg-white rounded-full p-3 shadow-lg hover:bg-gold hover:text-white transition-all duration-300"
                aria-label="Previous benefit"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>

              {/* Next Button */}
              <button
                onClick={() => setCurrentBenefitIndex((prev) => (prev === benefits.length - 1 ? 0 : prev + 1))}
                className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 sm:translate-x-12 z-10 bg-white rounded-full p-3 shadow-lg hover:bg-gold hover:text-white transition-all duration-300"
                aria-label="Next benefit"
              >
                <ChevronRight className="w-6 h-6" />
              </button>

              {/* Carousel Card */}
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentBenefitIndex}
                  initial={{ opacity: 0, x: 100 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -100 }}
                  transition={{ duration: 0.3 }}
                  className="bg-white rounded-lg shadow-xl p-8 sm:p-12 min-h-[300px] flex flex-col justify-center"
                >
                  <div className="flex items-start gap-6">
                    <div className="flex-shrink-0">
                      <CheckCircle className="w-16 h-16 text-gold" strokeWidth={1.5} />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-heading text-3xl font-semibold text-olive-darkest mb-4">
                        {benefits[currentBenefitIndex].title}
                      </h3>
                      <p className="text-lg text-gray-700 leading-relaxed">
                        {benefits[currentBenefitIndex].description}
                      </p>
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>

              {/* Dots Indicator */}
              <div className="flex justify-center gap-2 mt-8">
                {benefits.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentBenefitIndex(index)}
                    className={`h-2 rounded-full transition-all duration-300 ${
                      index === currentBenefitIndex 
                        ? 'w-8 bg-gold' 
                        : 'w-2 bg-gray-300 hover:bg-gold/50'
                    }`}
                    aria-label={`Go to benefit ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-16 bg-olive-darkest text-white p-8 sm:p-10 md:p-12 rounded-xl shadow-2xl text-center"
          >
            <p className="text-base sm:text-lg leading-relaxed mb-8 text-white">
              Say goodbye to unwanted pigmentation and reveal clearer, more even-toned skin with our advanced pigmented lesion reduction treatments!
            </p>
            <motion.button
              whileHover={{ y: -2, scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
              onClick={scrollToContact}
              className="w-full sm:w-auto bg-gold text-olive-darkest px-8 sm:px-10 md:px-12 py-3 sm:py-4 rounded-lg font-semibold text-base sm:text-lg tracking-wide hover:bg-white transition-all duration-300 shadow-lg hover:shadow-xl touch-manipulation mb-10"
            >
              Schedule Consultation
            </motion.button>

            <div className="w-24 h-0.5 bg-gold/30 mx-auto mb-8"></div>

            <h3 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold mb-4 text-white">Have Questions?</h3>
            <p className="text-base sm:text-lg text-white mb-6 sm:mb-8 max-w-2xl mx-auto leading-relaxed">
              Learn about pigmented lesion reduction, treatment expectations, and how we can help you achieve clearer skin.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <motion.button
                whileHover={{ y: -2, scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => {
                  navigate('/faq');
                  setTimeout(() => {
                    const element = document.getElementById('pigmented-lesion');
                    if (element) {
                      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                  }, 100);
                }}
                className="w-full sm:w-auto bg-white/10 text-white border-2 border-white px-8 sm:px-10 md:px-12 py-3 sm:py-4 rounded-lg font-semibold text-base sm:text-lg tracking-wide hover:bg-white hover:text-olive-darkest transition-all duration-300 shadow-lg hover:shadow-xl touch-manipulation"
              >
                View FAQs
              </motion.button>
              <motion.button
                whileHover={{ y: -2, scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                onClick={scrollToContact}
                className="w-full sm:w-auto bg-white/10 text-white border-2 border-gold px-8 sm:px-10 md:px-12 py-3 sm:py-4 rounded-lg font-semibold text-base sm:text-lg tracking-wide hover:bg-gold hover:text-olive-darkest transition-all duration-300 shadow-lg hover:shadow-xl touch-manipulation"
              >
                Contact Us Directly
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
    </>
  );
};

export default PigmentedLesion;